import React, { useContext, useRef, useState } from 'react';

import { makeStyles } from '@material-ui/core/styles';
import Avatar from '@material-ui/core/Avatar';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import CssBaseline from '@material-ui/core/CssBaseline';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import Link from '@material-ui/core/Link';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';

import AuthContext from '../../services/firebase/AuthContext';
import Copyright from '../Copyright';
// import { DbxContext } from '../../services/DbxContext';
import { FirebaseContext } from '../../services/firebase/FirebaseContext';
import FireDataContext from '../../services/firebase/FireDataContext';
import SolicitorList from './SolicitorList';
import { timestamp } from '../../app/data/validate';

const sanitize = object => {
  const copy = {}

  for (const field in object) {
    copy[field] = object[field] ?? '';
  }

  return copy;
}

const useStyles = makeStyles((theme) => ({
  paper: {
    marginTop: theme.spacing(8),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: theme.palette.secondary.main,
  },
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
}));

const SelectFirmPage = ({ user }) => {
  const firebase = useContext(FirebaseContext);
  const findSolicitor = firebase.functions().httpsCallable('findSolicitor');
  const { currentUser } = useContext(AuthContext);

  // user can be optionally supplied to support TICIT admin creating referrer info
  const { uid } = user || currentUser;
  const userIsReferrer = uid === currentUser.uid;

  // const cache = useContext(DbxContext);
  const db = useContext(FireDataContext);

  const [firms, setFirms] = useState();
  const [criteria, setCriteria] = useState('');
  const [waiting, setWaiting] = useState(false);

  const handleQuery = () => {
    if (!queryRef.current.value) return;
    setCriteria(queryRef.current.value);
    (async () => {
      setWaiting(true)

      const query = {
        SearchText: queryRef.current.value,
        NumberOfResults: 25,
        Skip: 0
      };

      const result = await findSolicitor(JSON.stringify(query));
      const organisations = result.data.Organisations;

      // the order of the results returned is a bit unintuitive due to the lexicographical analysis
      // i.e. things that seem like exact matches to a user don't come first e.g.
      // in a search for MASON BAGGOTT the firm comes about 7th in the results and MASON SOLICITORS LIMITED comes first 

      // so let's grab what appear to be good matches to a user using a regex 
      const rx = new RegExp(criteria, 'i');
      const matches = organisations.filter(org => rx.test(org.PracticeName));
      // and put the rest of the results after those
      setFirms([...matches, ...organisations.filter(org => !matches.find(m => m.Id === org.Id))].slice(0, 10));
      setWaiting(false);
    })();

  };

  const handleKeyPress = ({ key }) => {
    if (/enter/i.test(key)) {
      handleQuery();
    }
  }

  const handleOfficeSelection = ({ firm, office }) => {
    // create a composite id from the firm & office
    const id = `${firm.SraNumber}-${office.OfficeId}`;

    (() => {
      return new Promise(async (resolve, reject) => {
        try {
          var org = await db.get({ path: `organizations/${id}` });

          const address = sanitize(office);
          
          if (!org) {
            org = {
              name: `${firm.PracticeName} (${office.Town})`,
              adminOrganization: 'WHuoyDs7zcB8wUU7euiC',
              phoneNumber: office.PhoneNumber || '',
              type: 'cais-client',
              office: address,
              address: {
                lines: [
                  address.Address1,
                  address.Address2,
                  address.Address3,
                  address.Address4,
                ],
                postcode: office.Postcode
              },
              firm: sanitize(firm),
              ts: timestamp(),
              uid: currentUser.uid,
            }
            
            await db.update({
              path: `organizations/${id}`,
              payload: org
            })
          }
          
          // update the user's org so they can get out of limbo :)
          await db.update({
            path: `users/${uid}`,
            payload: {
              organization: id,
              ts: timestamp(),
            }
          });
          
          // this org/member info should be deprecated - it was required when we used to add referrers to firms before they signed up but ...
          // ... now we have self-service sign-up for solicitors so ... what?
          if (userIsReferrer) {
            currentUser.organizationId = id;

            await db.update({
              path: `organizations/${id}/members/${uid}`,
              payload: {
                email: currentUser.email,
                uid: currentUser.uid,
                ts: timestamp(),
              }
            });

            await db.remove({ path: `organizations/0000/members/${currentUser.uid}` });
          }

          resolve(true);
        }
        catch (error) {
          reject(error);
        }
      })
    })()
      // navigate didn't seem to trigger a refresh of App routes so let's brute force the issue :O 
      .then(() => {
        window.location.replace("/")
      });


  }

  const classes = useStyles();

  const queryRef = useRef();

  const FindFirmForm = () => {
    return (
      <div className={classes.form}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              onKeyPress={handleKeyPress}
              inputRef={queryRef}
              defaultValue={criteria}
              name="queryText"
              variant="outlined"
              fullWidth
              id="queryText"
              label="Firm Name or SRA Number"
              autoFocus
              helperText="You can search for all or any part of your firm name"
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          {
            waiting && <LinearProgress style={{ width: '100%', marginTop: '1rem', marginBottom: '1rem' }} />
          }
          {
            !waiting && <SolicitorList data={firms} onSelect={handleOfficeSelection} />
          }
        </Grid>
        {
          !waiting &&
          <Button
            onClick={handleQuery}
            fullWidth
            variant="contained"
            color="primary"
            className={classes.submit}
          >Find your firm
          </Button>
        }
        <Grid container justify="flex-end">
          <Grid item>
            <Link href="/signin" variant="body2">
              Already have an account? Sign in
            </Link>
          </Grid>
        </Grid>
      </div>
    )

  }

  return (
    <Container component="main" maxWidth="xs">
      <CssBaseline />
      <div className={classes.paper}>
        <Typography component="h1" variant="h6">The Intermediary Cooperative</Typography>
        <Avatar className={classes.avatar}>
          <PersonAddIcon />
        </Avatar>
        <Typography component="h1" variant="h5">Select your Law Firm</Typography>
        <FindFirmForm />
      </div>
      <Box mt={5}>
        <Copyright />
      </Box>
    </Container>
  );

}

const SelectFirmPagePath = '/signup/solicitors';

export {
  SelectFirmPagePath
}

export default SelectFirmPage;